import * as React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { alpha, useTheme } from '@mui/material/styles'
import { parseISO } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { resolveEventColor, isSolidInk } from '../../eventColors'
import { eventAccessibleName } from '../../eventLabel'
import { tileTime } from '../../dateFormat'
import { useOpenEventDetail } from '../EventDetailPopover/selectionContext'

// M1 live states: a timed tile inside a week day-column, and an all-day chip in
// the week header. `monthChip` is scaffolded for the M2 month grid.
export type EventTileVariant = 'timedWeek' | 'allDayWeek' | 'monthChip'

/** Absolute-positioning geometry (left/right/top/height/width) for `timedWeek`. */
export type EventTileGeometry = {
  left?: number | string
  right?: number | string
  top?: number
  height?: number
  width?: number | string
}

type Props = {
  event: CalendarEvent
  variant: EventTileVariant
  geometry?: EventTileGeometry
  /** The event runs on past this tile's left edge (a bar cut at a row boundary). */
  continuesBefore?: boolean
  /** The event runs on past this tile's right edge. */
  continuesAfter?: boolean
}

const RADIUS: Record<EventTileVariant, string> = {
  timedWeek: '4px',
  allDayWeek: '3px',
  monthChip: '4px',
}

const PADDING: Record<EventTileVariant, string> = {
  timedWeek: '2px 6px',
  allDayWeek: '1px 6px',
  monthChip: '2px 6px',
}

// A ButtonBase so clicking opens the detail popover (SH-1986) and focus returns
// to the tile on close. `stopPropagation` keeps the click off the grid's
// create-on-click target behind it (SPEC-EventTile §v3).
function EventTileImpl({
  event,
  variant,
  geometry,
  continuesBefore,
  continuesAfter,
}: Props) {
  const theme = useTheme()
  const color = resolveEventColor(event, theme)
  const solid = isSolidInk(event)
  const openDetail = useOpenEventDetail()

  const showMeta = variant === 'timedWeek' && !event.allDay
  const metaColor = solid
    ? alpha(theme.palette.common.white, 0.85)
    : theme.palette.text.secondary

  const radius = RADIUS[variant]
  const borderRadius = `${continuesBefore ? 0 : radius} ${continuesAfter ? 0 : radius} ${continuesAfter ? 0 : radius} ${continuesBefore ? 0 : radius}`

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    openDetail(event, e.currentTarget)
  }

  return (
    <ButtonBase
      focusRipple
      onClick={handleClick}
      aria-label={eventAccessibleName(event)}
      aria-haspopup="dialog"
      data-test-id="event-tile"
      sx={{
        display: 'block',
        textAlign: 'left',
        boxSizing: 'border-box',
        overflow: 'hidden',
        width: variant === 'timedWeek' ? undefined : '100%',
        background: color.fill,
        borderLeft:
          solid || continuesBefore ? undefined : `3px solid ${color.border}`,
        borderRadius,
        padding: PADDING[variant],
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        ...(geometry && {
          position: 'absolute',
          left: geometry.left,
          right: geometry.right,
          top: geometry.top,
          height: geometry.height,
          width: geometry.width,
        }),
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: '12px',
          fontWeight: 600,
          color: color.ink,
          lineHeight: 1.3,
          display: 'block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.title}
      </Box>

      {showMeta && (
        <Box
          component="span"
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '11px',
            fontWeight: 400,
            color: metaColor,
            lineHeight: 1.25,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tileTime(parseISO(event.start))}
        </Box>
      )}
    </ButtonBase>
  )
}

function sameGeometry(a?: EventTileGeometry, b?: EventTileGeometry): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.left === b.left &&
    a.right === b.right &&
    a.top === b.top &&
    a.height === b.height &&
    a.width === b.width
  )
}

// Geometry is recomputed into a fresh object on every parent render (e.g. a filter
// toggle), so a value comparison — not the default shallow ref check — is what
// lets an unchanged tile skip re-parsing its dates and re-resolving its colour.
export const EventTile = React.memo(
  EventTileImpl,
  (prev, next) =>
    prev.event === next.event &&
    prev.variant === next.variant &&
    prev.continuesBefore === next.continuesBefore &&
    prev.continuesAfter === next.continuesAfter &&
    sameGeometry(prev.geometry, next.geometry),
)
